import clsx from "clsx";
import { type ReactNode, useId } from "react";
import Card, { type CardProps } from "../../common/Card/Card";
import styles from "../RadioCards/RadioCards.module.scss";
import Radio from "./Radio";
import { useRadioGroup } from "./RadioGroup";

export interface RadioCardProps<TValue extends string = string> extends Omit<CardProps, "children"> {
  value: TValue;
  label: ReactNode;
  description?: ReactNode;
  details?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  children?: ReactNode;
}

const RadioCard = <TValue extends string = string>({
  value,
  label,
  description,
  details,
  icon,
  disabled = false,
  children,
  className,
  ...cardProps
}: RadioCardProps<TValue>) => {
  const group = useRadioGroup();
  if (!group) throw new Error("RadioCard must be rendered inside RadioGroup.");

  const inputId = useId();
  const selected = group.value === value;
  const optionDisabled = group.disabled || disabled;

  return (
    <label
      className={styles.choice}
      data-radio-card-option
      data-selected={selected ? "true" : "false"}
      data-disabled={optionDisabled ? "true" : undefined}
      htmlFor={inputId}
    >
      <Card
        {...cardProps}
        className={clsx(styles.option, className)}
        data-selected={selected ? "true" : "false"}
        data-disabled={optionDisabled ? "true" : undefined}
      >
        {icon ? <span className={styles.icon}>{icon}</span> : null}
        <span className={styles.body}>
          <span data-radio-card-label className={styles.label}>{label}</span>
          {description ? <span data-radio-card-description className={styles.description}>{description}</span> : null}
          {details ? <span data-radio-card-details className={styles.details}>{details}</span> : null}
          {children}
        </span>
        <Radio
          id={inputId}
          className={styles.indicator}
          value={value}
          disabled={optionDisabled}
        />
      </Card>
    </label>
  );
};

export default RadioCard;
